import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import MedAutocomplete from '../../../MedAutocomplete/MedAutocomplete';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { gatewayMapper } from '../../../../pages/Device/constants';
import { DeviceModels, MedisanteDevice } from '../../../../pages/Device/types';
import GatewayDevices from './GatewayDevices';
import { getBTDevices, getGatewayId } from './provider';
import {
  useGetDeviceQuery,
  useGetDevicesQuery,
  useLinkDeviceMutation,
  useUnLinkDeviceMutation,
} from '../../../../store/api-slice';
import { Filter } from '../../../../pages/Devices/types';

interface Props {
  initialDevices: MedisanteDevice[];
  open?: boolean;
  onClose: (isDirty: boolean) => void;
}

function DeviceLinking({ open = false, onClose, initialDevices }: Props) {
  const isDirty = useRef<boolean>(false);
  const { apiBaseURL } = useConfig();
  const { authUser } = useAuthUser();
  const [linkDevices, linkingStatus] = useLinkDeviceMutation();
  const [unlinkDevices, unlinkingStatus] = useUnLinkDeviceMutation();

  const initialGatewayId = useMemo(
    () => getGatewayId(initialDevices),
    [initialDevices]
  );
  const initialBluetoothDevices = useMemo(
    () => getBTDevices(initialDevices),
    [initialDevices]
  );

  const [gatewayId, setGatewayId] = useState(initialGatewayId);
  useEffect(() => {
    setGatewayId(initialGatewayId);
  }, [initialGatewayId]);

  const {
    data: gateway,
    isFetching: isGatewayLoading,
    refetch: fetchGateway,
  } = useGetDeviceQuery({ apiBaseURL, id: gatewayId });

  const initialGatewayModels = useMemo(() => {
    if (initialBluetoothDevices.length > 0) {
      const gwModel = gatewayMapper
        .filter((m) =>
          m.bluetoothModels.includes(
            initialBluetoothDevices[0].model as DeviceModels
          )
        )
        .flatMap((m) => m.gatewayModel);

      return gwModel;
    }
    return [] as DeviceModels[];
  }, [initialBluetoothDevices]);

  const { data: gatewayOptions = [] } = useGetDevicesQuery({
    apiBaseURL,
    orgs:
      initialDevices.length > 0
        ? [initialDevices[0].owner]
        : authUser.selectedOrgs,
    filters: initialGatewayModels.map((m) => {
      return {
        category: 'model',
        value: m,
      } as Filter;
    }),
  });

  const gatewayModel = gateway ? gateway.model : null;
  const gatewayOwner = gateway ? [gateway.owner] : [];

  const btModels = useMemo(() => {
    const gwModel = gatewayModel
      ? gatewayMapper.find((m) =>
          m.gatewayModel.includes(gatewayModel as DeviceModels)
        )
      : null;
    return !!gwModel ? gwModel.bluetoothModels : [];
  }, [gatewayModel]);

  const {
    data: btOptions,
    refetch: fetchBluetoothDevices,
    ...btDevicesStatus
  } = useGetDevicesQuery(
    {
      apiBaseURL,
      orgs: gatewayOwner,
      filters: [
        {
          category: 'noGateway',
          value: 'true',
        } as Filter,
        ...btModels.map((m) => {
          return {
            category: 'model',
            value: m,
          } as Filter;
        }),
      ],
    },
    {
      skip: !gateway,
    }
  );

  const [futureBTDevices, setFutureBTDevices] = useState(0);
  const [devicesToLink, setDevicesToLink] = useState<MedisanteDevice[]>([]);
  useEffect(() => {
    if (btOptions && btOptions.length > 0) {
      const devicesToLink = btOptions.filter(
        (o) =>
          !o.gatewayID && initialBluetoothDevices.some((d) => d.id === o.id)
      );
      setDevicesToLink(devicesToLink);
    } else if (btOptions && btOptions.length === 0) {
      setDevicesToLink(initialBluetoothDevices.filter((d) => !d.gatewayID));
    }
  }, [initialBluetoothDevices, btOptions]);

  const linkDevicesHandler = useCallback(async () => {
    if (gateway && open && devicesToLink.length > 0) {
      const numberOfChildren = gateway.sensors ? gateway.sensors!.length : 0;
      setFutureBTDevices(numberOfChildren + devicesToLink.length);
      await linkDevices({
        apiBaseURL,
        gatewayId: gateway.id!,
        deviceIds: devicesToLink,
      });
      fetchGateway();
      fetchBluetoothDevices();
      isDirty.current = true;
      setDevicesToLink([]);
    }
  }, [
    apiBaseURL,
    open,
    gateway,
    linkDevices,
    devicesToLink,
    fetchGateway,
    fetchBluetoothDevices,
  ]);

  const unlinkDevicesHandler = useCallback(
    async (devices: MedisanteDevice[]) => {
      if (gateway) {
        const numberOfChildren = gateway.sensors ? gateway.sensors!.length : 0;
        setFutureBTDevices(numberOfChildren - devices.length);
        await unlinkDevices({
          apiBaseURL,
          gatewayId: gateway.id!,
          deviceIds: devices,
        });
        fetchGateway();
        fetchBluetoothDevices();
        isDirty.current = true;
      }
    },
    [gateway, unlinkDevices, fetchGateway, fetchBluetoothDevices, apiBaseURL]
  );

  const isLoading =
    isGatewayLoading || linkingStatus.isLoading || unlinkingStatus.isLoading;

  return (
    <Dialog
      BackdropProps={{
        //@ts-ignore
        'data-testid': 'device-linking-overlay',
      }}
      aria-labelledby="dialog-title"
      open={open}
      onClose={() => {
        onClose(isDirty.current);
      }}
    >
      <DialogTitle>Device linking</DialogTitle>
      <DialogContent>
        {gatewayId ? (
          <GatewayDevices
            gateway={gateway!}
            loading={isLoading}
            unlinkHandler={unlinkDevicesHandler}
            numberOfDevicesToLink={futureBTDevices}
          />
        ) : null}

        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6">Link to gateway</Typography>
          </Grid>
          {!initialGatewayId ? (
            <Grid item xs={12}>
              <MedAutocomplete
                disableClearable
                placeholder="Gateway"
                options={gatewayOptions.filter((g) => !g.allowUnlinkedGateway)}
                getOptionSelected={(o1, o2) => o1?.id === o2?.id}
                optionLabelGetter={(o) => `${o?.model} - ${o?.serialNumber}`}
                value={gateway}
                onChange={(
                  event: React.ChangeEvent,
                  newValue: MedisanteDevice
                ) => setGatewayId(newValue.id)}
              />
            </Grid>
          ) : null}

          <Grid item xs={12} wrap="wrap" container>
            {!btDevicesStatus.isLoading ||
            !linkingStatus.isLoading ||
            !unlinkingStatus.isLoading ? (
              <>
                <Grid item style={{ flexGrow: 1 }}>
                  <MedAutocomplete
                    disabled={!gatewayId}
                    multiple
                    placeholder="Devices to link"
                    getOptionSelected={(o1, o2) => o1?.id === o2?.id}
                    options={btOptions || []}
                    optionLabelGetter={(o) => `${o.model} - ${o.serialNumber}`}
                    value={devicesToLink}
                    onChange={(
                      event: React.ChangeEvent,
                      newValue: MedisanteDevice[]
                    ) => setDevicesToLink(newValue)}
                  />
                </Grid>
                <Button
                  disabled={
                    !gateway ||
                    linkingStatus.isLoading ||
                    unlinkingStatus.isLoading ||
                    devicesToLink.length === 0
                  }
                  color="primary"
                  onClick={linkDevicesHandler}
                >
                  Link
                </Button>
              </>
            ) : (
              <Skeleton variant="rect" height="45px" width="100%" />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose(isDirty.current);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeviceLinking;
